import { API } from 'network/api';
import { useState, useEffect, useCallback, useRef } from 'react';

const usePipeData = (params) => {
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isFetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // Keep track of the current request ID to prevent stale data updates
  const currentRequestIdRef = useRef(0);
  const abortControllerRef = useRef(new AbortController());

  const DATA_PER_PAGE = 30;
  const REQUEST_DELAY_MS = 500; // delay between requests

  // Function to add delay between requests
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Fetch data for a specific page
  const fetchPageData = async (options, pageIndex) => {
    return await API.get('/mrvresources', {
      params: {
        ...options,
        page: pageIndex,
        size: DATA_PER_PAGE,
      },
      signal: abortControllerRef.current?.signal,
    });
  };

  const handleError = (err) => {
    if (err?.name !== 'CanceledError') {
      const genericMsg = 'Something went wrong. Please try again';
      setError(
        err?.response?.status === 500 ? genericMsg : err?.message || genericMsg,
      );
    }
  };

  const fetchData = useCallback(
    async (options = params) => {
      if (!options?.['seasonTypeId.equals']) return;

      // Cancel any ongoing requests
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      // Generate a new request ID for this fetch operation - Done inorder to prevent race conditions
      const requestId = ++currentRequestIdRef.current;

      // Reset all states
      setData([]);
      setError(null);
      setFetching(true);
      setLoading(true);
      setProgress(0);
      setCurrentPage(0);
      setTotalPages(0);

      try {
        // Check if this request is still relevant
        if (requestId !== currentRequestIdRef.current) return;

        // Fetch the first page to determine total count
        const initialResponse = await fetchPageData(options, 0);
        const totalCount =
          Number(initialResponse.headers['x-total-count']) || 0;

        // Check if this request is still relevant after first request
        if (requestId !== currentRequestIdRef.current) return;

        if (!initialResponse.data.length) {
          setError('No data available');
          setLoading(false);
          setData([]);
          return;
        }

        // Calculate total pages
        const pages = Math.ceil(totalCount / DATA_PER_PAGE);
        setTotalPages(pages);

        // Set initial data (completely replacing any previous data)
        let accumulatedData = [...initialResponse.data];
        setData(accumulatedData);
        setCurrentPage(1);
        setProgress((1 / pages) * 100);
        let page = 1;

        // Fetch remaining pages sequentially with delay
        while (page < pages) {
          // Check if the request has been canceled
          if (
            abortControllerRef.current.signal.aborted ||
            requestId !== currentRequestIdRef.current
          ) {
            break;
          }

          // Add delay before next request
          await delay(REQUEST_DELAY_MS);

          // Check again if aborted after delay
          if (
            abortControllerRef.current.signal.aborted ||
            requestId !== currentRequestIdRef.current
          ) {
            break;
          }

          if (page + 2 < pages) {
            accumulatedData = await Promise.all([
              fetchPageData(options, page),
              fetchPageData(options, page + 1),
            ]).then((data) =>
              data.reduce((prev, current) => 
                [...prev, ...current.data]
              , accumulatedData),
            );
            page += 2;
          } else {
            const res = await fetchPageData(options, page);
            accumulatedData = [...accumulatedData, ...res.data];
            page++;
          }

          // Check if this request is still relevant
          if (requestId !== currentRequestIdRef.current) return;

          // Update with new accumulated data
          setData(accumulatedData);
          setCurrentPage(page);
          setProgress((page / pages) * 100);
        }
      } catch (err) {
        // Only handle error if this request is still relevant
        if (requestId === currentRequestIdRef.current) {
          handleError(err);
        }
      } finally {
        // Only update loading states if this request is still relevant
        if (requestId === currentRequestIdRef.current) {
          setLoading(false);
          setFetching(false);
        }
      }
    },
    [params],
  );

  // Fetch data on params change
  useEffect(() => {
    fetchData();

    return () => {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    };
  }, [fetchData]);

  return {
    isLoading,
    data,
    error,
    fetchData,
    isFetching,
    progress,
    totalPages,
    currentPage,
  };
};

export default usePipeData;
